"use client";

import { useState } from "react";
import { createPortal } from "react-dom";
import { ChevronUp, ArrowRight } from "lucide-react";

import { Button } from "@/app/components/shadcn/button";
import OrderSummary from "./OrderSummary";
import AuthRequiredContent from "./AuthRequiredContent";

import useAuth from "@/app/lib/features/auth/auth.hook";
import { useRouter } from "next/navigation";
import { trackGeneric } from "@/app/lib/tracking/interactions";

import {
  CartBox,
  SummaryMessage,
  DiscountInputText,
  CouponsInfo,
  AppliedDiscount,
} from "@/app/lib/features/cart/cart.types";

interface BasketBottomBarProps {
  cartBox: CartBox[];
  summaryMessages: SummaryMessage[];
  discountInputText?: DiscountInputText;
  couponsInfo?: CouponsInfo;
  appliedDiscounts?: AppliedDiscount[];
}

const BasketBottomBar = ({
  cartBox,
  summaryMessages,
  discountInputText,
  couponsInfo,
  appliedDiscounts = [],
}: BasketBottomBarProps) => {
  const [sheetOpen, setSheetOpen] = useState(false);
  const [authOpen, setAuthOpen] = useState(false);

  const { isAuthenticated } = useAuth();
  const router = useRouter();

  const total = cartBox.find((b) => b.is_total);

  const freeShipping = summaryMessages.find(
    (m) =>
      m.text?.toLowerCase().includes("kargo bedava") ||
      m.text?.toLowerCase().includes("ücretsiz kargo"),
  );

  const handleCheckout = () => {
    trackGeneric("cart_checkout", "checkout");
    // Özet sheet'i açıkken tetiklenmiş olabilir; auth sheet'i onun üstüne
    // binmesin diye önce kapat. Bar'dan tetiklendiğinde zaten kapalı.
    setSheetOpen(false);
    if (isAuthenticated) {
      router.push("/account/checkout");
      return;
    }
    setAuthOpen(true);
  };

  const backdropVisible = sheetOpen || authOpen;

  // 👇 SSR SAFE PORTAL GUARD
  if (typeof window === "undefined") return null;

  return createPortal(
    <>
      {/* BACKDROP */}
      <div
        className="fixed inset-0 bg-black/50 z-50"
        style={{
          opacity: backdropVisible ? 1 : 0,
          pointerEvents: backdropVisible ? "auto" : "none",
          transition: "opacity 0.2s ease",
        }}
        onClick={() => {
          setSheetOpen(false);
          setAuthOpen(false);
        }}
      />

      {/* 📱 BOTTOM BAR */}
      <div className="lg:hidden fixed bottom-0 left-0 right-0 z-40 bg-white border-t pb-safe">
        <div className="flex items-center h-16 px-4 gap-3">
          <button onClick={() => setSheetOpen(true)}>
            <div className="flex items-center gap-1">
              <ChevronUp size={14} />
              <span className="font-bold">{total?.price}</span>
            </div>

            {freeShipping && (
              <span className="text-xs text-green-600">Kargo Bedava</span>
            )}
          </button>

          <Button onClick={handleCheckout} className="flex-1 h-11">
            Sepeti Onayla
            <ArrowRight size={16} />
          </Button>
        </div>
      </div>

      {/* 📦 SHEET */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl max-h-[85vh] flex flex-col pb-safe"
        style={{
          transform: sheetOpen ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.35s cubic-bezier(0.2, 0, 0, 1)",
        }}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <div className="flex justify-between px-4 py-3 border-b">
          <h2 className="font-semibold">Sipariş Özeti</h2>
          <button onClick={() => setSheetOpen(false)}>✕</button>
        </div>

        <div className="overflow-y-auto overscroll-contain p-4 pb-6">
          <OrderSummary
            cartBox={cartBox}
            summaryMessages={summaryMessages}
            discountInputText={discountInputText}
            couponsInfo={couponsInfo}
            appliedDiscounts={appliedDiscounts}
            continueShoppingUrl="/"
            flat
            // hideButton yok: "Siparişi Tamamla" + "Alışverişe Devam Et"
            // butonları desktop özetiyle aynı yerden geliyor.
            // onCheckoutClick verilince buton "Siparişi Tamamla" olur ve
            // auth kontrolü çalışır — hideButton'suz varsayılan hali
            // doğrudan /account/checkout'a Link atıp misafiri kaçırırdı.
            onCheckoutClick={handleCheckout}
          />
        </div>
      </div>

      {/* 🔐 AUTH SHEET */}
      <div
        className="fixed bottom-0 left-0 right-0 z-50 bg-white rounded-t-2xl max-h-[85vh] flex flex-col pb-safe"
        style={{
          transform: authOpen ? "translateY(0)" : "translateY(100%)",
          transition: "transform 0.35s cubic-bezier(0.2, 0, 0, 1)",
        }}
      >
        <div className="flex justify-center pt-3 pb-1">
          <div className="w-10 h-1 bg-gray-300 rounded-full" />
        </div>

        <AuthRequiredContent />
      </div>
    </>,
    document.body,
  );
};

export default BasketBottomBar;